"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent } from "@workspace/ui/components/dialog";
import posthog from "posthog-js";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { LEAD_FORM_ANCHOR, type LeadFormLocation } from "./content";
import { LeadForm, type LeadFormStage } from "./lead-form";
type LeadFormDialogContextValue = {
  isOpen: boolean;
  open: (location: LeadFormLocation) => void;
};
const LeadFormDialogContext = createContext<LeadFormDialogContextValue | null>(null);
export function useLeadFormDialog() {
  const context = useContext(LeadFormDialogContext);
  if (!context) {
    throw new Error("useLeadFormDialog must be used within LeadFormDialogProvider");
  }
  return context;
}

/**
 * Mounts the dialog only after the first CTA click so the form is not in the
 * initial HTML. Stage lives here because Radix unmounts dialog content when
 * it closes; keeping it in the provider lets a visitor resume step two
 * instead of creating another lead.
 */
export function LeadFormDialogProvider(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [location, setLocation] = useState("hero");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "contact"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [stage, setStage] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function open(nextLocation) {
      setLocation(nextLocation);
      setHasOpened(true);
      setIsOpen(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const open = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (window.location.hash !== LEAD_FORM_ANCHOR) {
        return;
      }
      setLocation("final");
      setHasOpened(true);
      setIsOpen(true);
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== isOpen || $[5] !== location) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      posthog.capture("business_lead_form_viewed", {
        placement: location
      });
    };
    t6 = [isOpen, location];
    $[4] = isOpen;
    $[5] = location;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== isOpen) {
    t7 = {
      isOpen,
      open
    };
    $[8] = isOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== hasOpened || $[11] !== isOpen || $[12] !== location || $[13] !== stage) {
    t8 = hasOpened && <Dialog open={isOpen} onOpenChange={setIsOpen}><DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-[#262321] bg-[#332F2C] text-white sm:max-w-lg"><LeadForm onStageChange={setStage} placement={location} stage={stage} /></DialogContent></Dialog>;
    $[10] = hasOpened;
    $[11] = isOpen;
    $[12] = location;
    $[13] = stage;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== children || $[16] !== t7 || $[17] !== t8) {
    t9 = <LeadFormDialogContext.Provider value={t7}>{children}{t8}</LeadFormDialogContext.Provider>;
    $[15] = children;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}